import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ShieldAlert, Plus, Trash2, Save, RotateCcw, ChevronDown } from 'lucide-react';
import { useMailFolders } from '../context/MailFoldersContext';
import SelectField from './SelectField';

const FIELD_OPTIONS = [
  { value: 'subject', label: 'Betreff enthält' },
  { value: 'from', label: 'Absender enthält' },
  { value: 'domain', label: 'E-Mail-Domain enthält' },
];

const FIELD_HINTS = {
  subject: 'Wörter im Betreff, z. B. Gewinnspiel, Viagra',
  from: 'Name oder E-Mail-Adresse des Absenders',
  domain: 'Teil der Domain nach dem @, z. B. .ru oder spammail.com',
};

const SPAM_COLLAPSE_KEY = 'settingsSpamFilterExpanded';

const emptyRule = () => ({
  id: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  label: '',
  field: 'subject',
  pattern: '',
  matchMode: 'contains',
  enabled: true,
});

const normalizeDraftRule = (r, i) => ({
  id: String(r.id || `rule-${i + 1}`),
  label: String(r.label || ''),
  field: ['subject', 'from', 'domain'].includes(r.field) ? r.field : 'subject',
  pattern: String(r.pattern || ''),
  matchMode: r.matchMode === 'regex' ? 'regex' : 'contains',
  enabled: r.enabled !== false,
});

const readExpanded = () => {
  try {
    const raw = localStorage.getItem(SPAM_COLLAPSE_KEY);
    if (raw == null) return true;
    return raw === '1' || raw === 'true';
  } catch (_) {
    return true;
  }
};

const SpamRulesSettings = ({ embedded = false }) => {
  const { spamRules, saveSpamRules, refreshSpamRules, resetSpamRulesDefaults } = useMailFolders();
  const [draft, setDraft] = useState({ enabled: true, rules: [] });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expanded, setExpanded] = useState(() => embedded || readExpanded());

  useEffect(() => {
    setDraft({
      enabled: spamRules.enabled !== false,
      rules: (spamRules.rules || []).map(normalizeDraftRule),
    });
    setDirty(false);
  }, [spamRules]);

  useEffect(() => {
    if (embedded) return;
    try {
      localStorage.setItem(SPAM_COLLAPSE_KEY, expanded ? '1' : '0');
    } catch (_) {}
  }, [expanded, embedded]);

  useEffect(() => {
    if (embedded) {
      setExpanded(true);
      return;
    }
    if (window.location.hash === '#spam-filter') {
      setExpanded(true);
      document.getElementById('spam-filter')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [embedded]);

  const ruleCount = (draft.rules || []).length;
  const canCollapse = !embedded && ruleCount > 0;
  const bodyOpen = embedded || !canCollapse || expanded;

  const toggleExpanded = () => {
    if (!canCollapse) return;
    setExpanded((v) => !v);
  };

  const updateRule = (idx, patch) => {
    setDraft((prev) => ({
      ...prev,
      rules: prev.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    }));
    setDirty(true);
  };

  const removeRule = (idx) => {
    setDraft((prev) => ({
      ...prev,
      rules: prev.rules.filter((_, i) => i !== idx),
    }));
    setDirty(true);
  };

  const addRule = () => {
    setDraft((prev) => ({
      ...prev,
      rules: [...prev.rules, emptyRule()],
    }));
    setDirty(true);
    setExpanded(true);
  };

  const handleSave = async () => {
    const rules = draft.rules
      .map((r) => ({
        ...r,
        pattern: String(r.pattern || '').trim(),
        label: String(r.label || '').trim(),
        matchMode: 'contains',
      }))
      .filter((r) => r.pattern);

    try {
      setSaving(true);
      await saveSpamRules({ enabled: draft.enabled !== false, rules });
      await refreshSpamRules();
      setDirty(false);
      toast.success('Spam-Filter gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = async () => {
    if (
      !window.confirm(
        'Standard-Spam-Regeln neu laden? Eigene (zusätzliche) Regeln bleiben erhalten.'
      )
    ) {
      return;
    }
    try {
      setResetting(true);
      await resetSpamRulesDefaults({ keepCustom: true });
      await refreshSpamRules();
      setExpanded(true);
      toast.success('Standardregeln aktualisiert');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Zurücksetzen fehlgeschlagen');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div
      id="spam-filter"
      className={embedded ? 'card fade-in overflow-hidden' : 'card fade-in scroll-mt-6 overflow-hidden'}
    >
      <div className="card-header">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <button
            type="button"
            onClick={toggleExpanded}
            disabled={!canCollapse}
            className={`min-w-0 text-left flex-1 ${
              canCollapse ? 'cursor-pointer' : 'cursor-default'
            }`}
            aria-expanded={bodyOpen}
            aria-controls="spam-filter-body"
          >
            <div className="flex items-center gap-2 min-w-0">
              <ShieldAlert className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
              <h3 className="text-base sm:text-lg font-medium text-neutral-900 dark:text-neutral-100 truncate">Spam-Filter</h3>
              {canCollapse ? (
                <span className="text-[11px] tabular-nums px-1.5 py-0.5 rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                  {ruleCount}
                </span>
              ) : embedded && ruleCount > 0 ? (
                <span className="text-[11px] tabular-nums px-1.5 py-0.5 rounded-md bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300">
                  {ruleCount}
                </span>
              ) : null}
              {canCollapse ? (
                <ChevronDown
                  className={`w-4 h-4 text-neutral-400 transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] ${
                    bodyOpen ? 'rotate-180' : ''
                  }`}
                />
              ) : null}
            </div>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 max-w-2xl">
              Automatische Textregeln für Phishing, Paketbetrug, Pharma, Krypto und mehr.
              Passende Mails wandern beim Aktualisieren des Posteingangs in den Spam-Ordner.
              „Kein Spam“ schützt einzelne Mails.
            </p>
          </button>
          <label
            className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300 cursor-pointer flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={draft.enabled !== false}
              onChange={(e) => {
                setDraft((prev) => ({ ...prev, enabled: e.target.checked }));
                setDirty(true);
              }}
              className="rounded border-neutral-300 dark:border-neutral-600 text-primary-600 focus:ring-primary-500"
            />
            Filter aktiv
          </label>
        </div>
      </div>

      <div
        id="spam-filter-body"
        className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          bodyOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        }`}
      >
        <div className="overflow-hidden min-h-0">
          <div
            className={`card-body space-y-4 transition-opacity duration-300 ${
              draft.enabled === false ? 'opacity-60' : ''
            } ${bodyOpen ? 'opacity-100' : 'opacity-0'}`}
          >
            {ruleCount === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 dark:border-neutral-700 px-4 py-8 text-center">
                <p className="text-sm text-gray-600 dark:text-gray-300">Noch keine Regeln.</p>
                <p className="text-xs text-gray-500 mt-1">
                  Lade die Standardregeln oder füge eigene hinzu.
                </p>
                <button
                  type="button"
                  onClick={handleResetDefaults}
                  disabled={resetting}
                  className="btn-secondary text-sm !py-2 mt-4 inline-flex items-center"
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" />
                  Standardregeln laden
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {draft.rules.map((rule, idx) => (
                  <div
                    key={rule.id || idx}
                    className="rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50/60 dark:bg-neutral-950/40 p-4 space-y-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">
                          {rule.label || `Regel ${idx + 1}`}
                        </p>
                        {String(rule.id || '').startsWith('default-') ? (
                          <p className="text-[11px] text-neutral-400 mt-0.5">Standardregel</p>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rule.enabled !== false}
                            disabled={draft.enabled === false}
                            onChange={(e) => updateRule(idx, { enabled: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          Aktiv
                        </label>
                        <button
                          type="button"
                          onClick={() => removeRule(idx)}
                          className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 touch-manipulation"
                          title="Regel löschen"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,14rem)_1fr] gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Prüfen in
                        </label>
                        <SelectField
                          value={rule.field || 'subject'}
                          disabled={draft.enabled === false}
                          onChange={(e) => updateRule(idx, { field: e.target.value })}
                          className="input-field !py-2 !text-sm"
                        >
                          {FIELD_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </SelectField>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Text / Wörter
                        </label>
                        <input
                          type="text"
                          value={rule.pattern || ''}
                          disabled={draft.enabled === false}
                          onChange={(e) =>
                            updateRule(idx, { pattern: e.target.value, matchMode: 'contains' })
                          }
                          placeholder={FIELD_HINTS[rule.field] || 'Wort eingeben…'}
                          className="input-field !py-2 !text-sm"
                        />
                        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                          Mehrere Begriffe mit Komma trennen. Groß-/Kleinschreibung egal.
                          {rule.matchMode === 'regex' ? (
                            <span className="block mt-0.5 text-amber-700 dark:text-amber-300">
                              Alte Experten-Regel — wird beim Speichern in eine einfache Textsuche
                              umgewandelt.
                            </span>
                          ) : null}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="settings-actions-row">
              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={addRule}
                  disabled={draft.enabled === false}
                  className="btn-secondary text-sm !py-2.5 sm:!py-2 inline-flex items-center justify-center disabled:opacity-50"
                >
                  <Plus className="w-4 h-4 mr-1.5" />
                  Regel hinzufügen
                </button>
                <button
                  type="button"
                  onClick={handleResetDefaults}
                  disabled={resetting || draft.enabled === false}
                  className="btn-secondary text-sm !py-2.5 sm:!py-2 inline-flex items-center justify-center disabled:opacity-50"
                  title="Aktuelle Standardregeln laden (eigene Regeln bleiben)"
                >
                  <RotateCcw className={`w-4 h-4 mr-1.5 ${resetting ? 'animate-spin' : ''}`} />
                  <span className="sm:hidden">Standard</span>
                  <span className="hidden sm:inline">Standard aktualisieren</span>
                </button>
              </div>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !dirty}
                className="btn-primary text-sm !py-2.5 sm:!py-2 inline-flex items-center justify-center disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="spinner-sm mr-2" />
                    Speichern…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1.5" />
                    <span className="sm:hidden">Speichern</span>
                    <span className="hidden sm:inline">Spam-Filter speichern</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpamRulesSettings;
